<template>
  <div class="tab-pane fade show active" id="audit_trail_tab">
    <section class="py-2 px-3">
      <div v-if="audit_trails.length === 0" class="fw-bold text-xs mb-2">
        No Audit Trail for this user found.
      </div>

      <ul v-else class="timeline text-xs">
        <li
          v-for="audit_trail in audit_trails"
          :key="audit_trail.id"
          class="timeline-item mb-5"
        >
          <p class="mb-2 text-xs fw-bold text-blue">
            {{ audit_trail.activity }}
          </p>
          <p class="text-muted mb-2 fw-bold">
            {{ formatDateTime(audit_trail.date_created) }}
          </p>

          <div class="d-flex align-items-center gap-2">
            <label
              v-if="shouldShowSuccessIcon(audit_trail)"
              class="bg-success text-white rounded-small px-3"
            >
              <i class="fa fa-check me-1" aria-hidden="true"></i>
            </label>
            <label
              v-if="shouldShowErrorIcon(audit_trail)"
              class="bg-danger text-white rounded-small px-3"
            >
              <i class="fa fa-exclamation-triangle me-1" aria-hidden="true"></i>
            </label>

            <a
              v-if="shouldShowConsentLink(audit_trail.activity)"
              @click="fetchAndShowConsentContent(audit_trail)"
              href="#"
              class="text-primary text-decoration-underline"
              :class="{ 'text-muted': loadingConsentContent }"
            >
              <i
                class="fa fa-spinner fa-spin me-1"
                v-if="loadingConsentContent"
              ></i>
              {{ loadingConsentContent ? "Loading..." : "View Content" }}
            </a>
          </div>
        </li>
      </ul>
    </section>
  </div>

  <transition name="modal">
    <div
      class="modal fade show d-block terms-modal"
      v-if="showConsentModal"
      tabindex="-1"
    >
      <div class="modal-dialog modal-lg modal-dialog-centered">
        <div class="modal-content terms-modal-content">
          <div class="modal-header terms-header">
            <div class="terms-title">
              <h5 class="modal-title">
                {{ modalContent?.consent_type || "Consent Content" }}
              </h5>
              <small class="text-muted"
                >By {{ modalContent?.auditInfo?.customer }} on
                {{ formatDateTime(modalContent?.auditInfo?.date) }}</small
              >
            </div>
            <button
              type="button"
              class="btn-close"
              @click="closeModal"
            ></button>
          </div>

          <div class="modal-body terms-body">
            <div class="terms-text" v-html="modalContent?.content"></div>
          </div>

          <div class="modal-footer terms-footer">
            <button
              type="button"
              class="btn btn-secondary btn-terms-decline"
              @click="closeModal"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>
<script>
import { API_BASE_URL } from "@/config";
import { TERMS_CONDITIONS_TEMPLATE } from "@/utils/constants.js";

export default {
  props: {
    audit_trails: {
      type: Array,
      default: () => [],
    },
    workflow_id: {
      type: [String, Number],
      required: true,
    },
  },
  data: () => ({
    modalContent: null,
    showConsentModal: false,
    loadingConsentContent: false,
    selectedAuditTrail: null,
  }),
  methods: {
    formatDateTime(date) {
      if (!date) return "N/A";
      const d = new Date(date);
      if (isNaN(d.getTime())) return "N/A";
      return d.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      });
    },
    shouldShowSuccessIcon(audit_trail) {
      const activityLower = audit_trail.activity.toLowerCase();

      if (audit_trail.status === "Success") {
        if (activityLower.includes("declined")) {
          return false;
        }
        return true;
      }

      return false;
    },
    shouldShowErrorIcon(audit_trail) {
      const activityLower = audit_trail.activity.toLowerCase();

      if (audit_trail.status === "error") {
        return true;
      }

      if (activityLower.includes("declined")) {
        return true;
      }

      return false;
    },
    shouldShowConsentLink(activity) {
      if (!this.hasConsentContent(activity)) {
        return false;
      }

      const activityLower = activity.toLowerCase();

      if (activityLower.includes("indemnity")) return true;
      if (activityLower.includes("marketing"))
        return activityLower.includes("accepted");

      return (
        activityLower.includes("accepted") ||
        activityLower.includes("agreed to terms and conditions")
      );
    },
    hasConsentContent(activity) {
      const consentActivities = [
        "Indemnity Notice",
        "Marketing Consent",
        "Terms and Conditions",
      ];
      return consentActivities.some((consActivity) =>
        activity.toLowerCase().includes(consActivity.toLowerCase())
      );
    },
    fetchAndShowConsentContent(auditTrail) {
      this.loadingConsentContent = true;
      this.selectedAuditTrail = auditTrail;

      const token = localStorage.getItem("token");
      const base_url = API_BASE_URL;

      if (
        auditTrail.activity
          .toLowerCase()
          .includes("agreed to terms and conditions")
      ) {
        const terms_url = `${base_url}/verify_api/customer_management_api/get_customer_terms_and_conditions/?workflow_id=${this.workflow_id}`;

        $.ajax({
          method: "GET",
          url: terms_url,
          headers: {
            Authorization: `Token ${token}`,
          },
          success: (response) => {
            let content = "";

            if (response.status === "terms_and_conditions_available") {
              content = `<iframe src="${response.terms_and_conditions_url}" class="terms-iframe" frameborder="0"></iframe>`;
            } else {
              content = TERMS_CONDITIONS_TEMPLATE;
            }

            this.modalContent = {
              content: content,
              consent_type: "Terms and Conditions",
              auditInfo: {
                activity: auditTrail.activity,
                date: auditTrail.date_created,
                status: auditTrail.status,
                customer: "Customer",
              },
            };

            this.showConsentModal = true;
            this.loadingConsentContent = false;
          },
          error: () => {
            Swal.fire({
              icon: "error",
              title: "Error",
              text: "Failed to load Terms and Conditions",
              confirmButtonText: "OK",
            });
            this.loadingConsentContent = false;
          },
        });

        return;
      }

      const consent_content_url = `${base_url}/verify_api/workflow_management_api/get_consent_content/${auditTrail.id}/`;

      $.ajax({
        method: "GET",
        url: consent_content_url,
        contentType: "application/json",
        dataType: "json",
        headers: {
          Authorization: `Token ${token}`,
        },
        success: (data) => {
          this.modalContent = {
            content: data.consent_content,
            consent_type: data.consent_type || "Consent Content",
            auditInfo: {
              activity: auditTrail.activity,
              date: auditTrail.date_created,
              status: auditTrail.status,
              customer: data.customer_name || "N/A",
            },
          };

          this.showConsentModal = true;
          this.loadingConsentContent = false;
        },
        error: (error) => {
          let error_message = "Error Getting Consent Content";
          if (error.responseJSON?.error) {
            error_message = error.responseJSON.error;
          }

          Swal.fire({
            icon: "error",
            title: "Error",
            text: error_message,
            confirmButtonText: "OK",
          });

          this.loadingConsentContent = false;
        },
      });
    },
    closeModal() {
      this.showConsentModal = false;
      this.modalContent = null;
      this.selectedAuditTrail = null;
    },
  },
};
</script>
